import React from "react";
import { get, includes, isNil, pick, size } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import Button from "components/Button";
import { Formik } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { capitalCase } from "change-case";
import { FiCalendar } from "react-icons/fi";

import InfoTooltip from "components/InfoTooltip";
import Alert from "components/Alert";
import {
  daysOfMonthOptions,
  END_OF_MONTH,
  PAYROLL_FREQUENCIES,
  hasMinimumScheduleValues,
  parseDayString,
  formatDate,
} from "utils/timeHelper";

import "./GroupForm.scss";
import { ScrollToFieldError } from "utils/form";
import PayPeriodExamples from "./PayPeriodExamples";

const defaultVals = [
  "name",
  "payrollFrequency",
  "associatedBankAccount",
  "anchorPayDate",
  "payrollProcessingOffset",
  "planType",
  "useUniqueInviteCodes",
];

const twicePerMonthVals = [...defaultVals, "dayOne", "dayTwo"];
const monthlyVals = [...defaultVals, "dayOne"];
const frequencyToNeededValMap = {
  WEEKLY: defaultVals,
  EVERY_OTHER_WEEK: defaultVals,
  MONTHLY: monthlyVals,
  TWICE_PER_MONTH: twicePerMonthVals,
};
const payrollProcessingOffset = [
  {
    value: 0,
    description: "Zero, Debit on the payroll date",
    key: "0",
  },
  {
    value: 1,
    description: "1 Day",
    key: "1",
  },
  {
    value: 2,
    description: "2 Days",
    key: "2",
  },
  {
    value: 3,
    description: "3 Days",
    key: "3",
  },
  {
    value: 4,
    description: "4 Days",
    key: "4",
  },
];

import * as yup from "yup";

const { TWICE_PER_MONTH, EVERY_OTHER_WEEK, WEEKLY, MONTHLY } =
  PAYROLL_FREQUENCIES;

function showDayOne(frequency: string | undefined) {
  return includes([MONTHLY, TWICE_PER_MONTH], frequency);
}

function showDayTwo(frequency: string | undefined) {
  return frequency === TWICE_PER_MONTH;
}

function requireDisplayAnchorPayDate(frequency: string | undefined) {
  return includes([EVERY_OTHER_WEEK, WEEKLY], frequency);
}

export const schema = yup.object({
  name: yup
    .string()
    .label("Group Name")
    .required()
    .min(2, "Must be at least two characters.")
    .max(100),
  payrollFrequency: yup.string().label("Payroll Frequency").required(),
  dayOne: yup.string().nullable().label("First Payday"),
  dayTwo: yup.string().nullable().label("Second Payday"),
  displayDayOne: yup
    .string()
    .nullable()
    .label("Alternate First Payday")
    .when(["useDisplayDates", "payrollFrequency"], {
      is: (useDisplayDates: boolean, payrollFrequency: string | undefined) =>
        useDisplayDates && showDayOne(payrollFrequency),
      then: (s) => s.label("Alternate First Payday").required(),
    }),
  displayDayTwo: yup
    .string()
    .nullable()
    .label("Alternate Second Payday")
    .when(["useDisplayDates", "payrollFrequency"], {
      is: (useDisplayDates: boolean, payrollFrequency: string | undefined) =>
        useDisplayDates && showDayTwo(payrollFrequency),
      then: (s) => s.label("Alternate Second Payday").required(),
    }),
  associatedBankAccount: yup
    .string()
    .label("Associated Bank Account")
    .required(),
  anchorPayDate: yup.date().label("First Pay Date").required(),
  payrollProcessingOffset: yup.number().label("Processing Offset").required(),
  planType: yup.string().label("Plan Type").required(),
  useUniqueInviteCodes: yup.boolean(),
  useDisplayDates: yup.boolean(),
  displayAnchorPayDate: yup
    .date()
    .when(["useDisplayDates", "payrollFrequency"], {
      is: (useDisplayDates: boolean, payrollFrequency: string | undefined) =>
        useDisplayDates && requireDisplayAnchorPayDate(payrollFrequency),
      then: (s) => s.label("End Date").required(),
    }),
});

const planTypeMapper: Record<string, string> = {
  statePlan: "STATE",
  primaryPlan: "PRIMARY",
  companionPlan: "COMPANION",
};

// `val` is a Formik date field (Date | string | nullish). The original calls
// Date.parse on it directly, so we keep the same loose boundary cast.
const parseDate = (val: Date | string | undefined | null) => {
  if (val) {
    return Date.parse(val as string);
  }
  return val;
};

/** A bank account option passed in via the `accounts` prop. */
interface GroupAccount {
  id: string;
  bankName?: string;
  bankAlias?: string;
  accountId?: string;
}

/** A plan-type option passed in via the `planTypes` prop. */
interface GroupPlanType {
  name: string;
}

interface GroupFormOwnProps {
  onClose?: () => void;
  onSubmit?: (group: Record<string, unknown>) => void;
  error?: string;
  accounts: GroupAccount[];
  isLoading?: boolean;
  group?: {
    id?: string;
    name?: string;
    payrollFrequency?: string;
    payrollProcessingOffset?: number;
    associatedLinkRequests?: unknown[];
    useUniqueInviteCodes?: boolean;
  };
  planTypes: GroupPlanType[];
  hasExpiredInvites?: boolean;
  enabledDaysBeforeDebit?: boolean;
}

interface GroupFormState {
  id: string;
  name: string | undefined;
  payrollFrequency: string | undefined;
  planType: string | undefined;
  associatedBankAccount: string | undefined;
  dayOne: number | string;
  dayTwo: number | string;
  anchorPayDate: Date | string | undefined;
  payrollProcessingOffset: number | undefined;
  displayAnchorPayDate: Date | string | undefined | null;
  displayDayOne: number | string;
  displayDayTwo: number | string;
  useUniqueInviteCodes: boolean | undefined;
}

/** Formik values: the component state plus the form-only `useDisplayDates`. */
interface GroupFormValues extends GroupFormState {
  useDisplayDates: boolean;
}

export default class GroupForm extends React.PureComponent<
  GroupFormOwnProps,
  GroupFormState
> {
  constructor(props: GroupFormOwnProps) {
    super(props);

    const offset = this.props.enabledDaysBeforeDebit ? undefined : 0;
    const uniqueCodes = this.props.hasExpiredInvites ? undefined : false;

    // We need to have this plan type here so it will submit both values correctly.
    const planTypeSize = size(this.props.planTypes) > 1;
    const planName = get(this.props.planTypes, "[0].name");
    const type = planTypeSize ? undefined : planTypeMapper[planName];
    const defaultBankId = get(props.accounts, "0.id", undefined);

    this.state = {
      id: "",
      name: undefined,
      payrollFrequency: undefined,
      planType: type,
      associatedBankAccount: defaultBankId,
      dayOne: 15,
      dayTwo: END_OF_MONTH,
      anchorPayDate: undefined,
      payrollProcessingOffset: offset,
      displayAnchorPayDate: undefined,
      displayDayOne: "",
      displayDayTwo: "",
      useUniqueInviteCodes: uniqueCodes,
    };
  }

  submitGroup = (values: GroupFormValues) => {
    const groupVals = this.getGroupValues(values);
    let group: Record<string, unknown> = {
      ...groupVals,
      anchorPayDate: formatDate(groupVals.anchorPayDate),
    };
    if (values.useDisplayDates) {
      group = this.addDisplayValues(group, values);
    }

    // Optional callback invoked unconditionally in the original.
    this.props.onSubmit!(group);
  };

  getGroupValues = (values: GroupFormValues) => {
    const groupVals = pick(
      values,
      frequencyToNeededValMap[
        values.payrollFrequency as keyof typeof frequencyToNeededValMap
      ]
    );
    if (values.payrollFrequency === TWICE_PER_MONTH) {
      return {
        ...groupVals,
        dayOne: parseDayString(values.dayOne),
        dayTwo: parseDayString(values.dayTwo),
      };
    } else if (values.payrollFrequency === MONTHLY) {
      return {
        ...groupVals,
        dayOne: parseDayString(values.dayOne),
      };
    }
    return groupVals;
  };

  addDisplayValues = (
    groupVals: Record<string, unknown>,
    values: GroupFormValues
  ) => {
    if (values.payrollFrequency === TWICE_PER_MONTH) {
      return {
        ...groupVals,
        displayDayOne: parseDayString(values.displayDayOne),
        displayDayTwo: parseDayString(values.displayDayTwo),
      };
    } else if (values.payrollFrequency === MONTHLY) {
      return {
        ...groupVals,
        displayDayOne: parseDayString(values.displayDayOne),
      };
    }
    return {
      ...groupVals,
      displayAnchorPayDate: formatDate(values.displayAnchorPayDate),
    };
  };
  render() {
    const planTypeSize = size(this.props.planTypes) > 1;

    const accounts = this.props.accounts.map((account, index) => {
      const label = !account.bankAlias
        ? account.bankName
        : `${account.bankName} - ${account.bankAlias} ${account.accountId} `;
      return (
        <option value={account.id} key={index}>
          {label}
        </option>
      );
    });

    return (
      <Formik<GroupFormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          this.submitGroup(values);
        }}
        enableReinitialize={true}
        initialValues={{
          ...this.state,
          useDisplayDates: false,
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit} className="group-creation">
            <ScrollToFieldError />
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formGroupName">
                <Form.Label>
                  What name do you want to give the group?
                </Form.Label>
                <Form.Control
                  name="name"
                  placeholder="Group Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.name && !!errors.name}
                  isValid={touched.name && !errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formGroupBank">
                <Form.Label>
                  Which bank account will this group use to process payroll?
                </Form.Label>
                <Form.Select
                  name="associatedBankAccount"
                  value={values.associatedBankAccount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    touched.associatedBankAccount &&
                    !!errors.associatedBankAccount
                  }
                  isValid={
                    touched.associatedBankAccount &&
                    !errors.associatedBankAccount
                  }
                >
                  <option value="" disabled>
                    Associated Bank Account
                  </option>
                  {accounts}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.associatedBankAccount}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formPlanType">
                <Form.Label>What is your plan type?</Form.Label>
                {planTypeSize && (
                  <Form.Select
                    className="planTypes"
                    name="planType"
                    value={values.planType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.planType && !!errors.planType}
                    isValid={touched.planType && !errors.planType}
                  >
                    <option value="">Select Plan Type</option>

                    {this.props.planTypes.map((item) => (
                      <option value={planTypeMapper[item.name]} key={item.name}>
                        {capitalCase(planTypeMapper[item.name])}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {!planTypeSize && (
                  <>
                    {this.props.planTypes.map((item) => (
                      <div
                        className="group-select"
                        // @ts-expect-error preserve existing quirk: `value` is not
                        // a valid attribute on a <div> and has no runtime effect.
                        value={planTypeMapper[item.name]}
                        key={item.name}
                      >
                        <Form.Control
                          name="planType"
                          value={capitalCase(planTypeMapper[item.name])}
                          onChange={handleChange}
                          isInvalid={touched.planType && !!errors.planType}
                          isValid={touched.planType && !errors.planType}
                          placeholder={capitalCase(planTypeMapper[item.name])}
                          disabled
                        />
                      </div>
                    ))}
                  </>
                )}

                <Form.Control.Feedback type="invalid">
                  {errors.planType}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formGroupFrequency">
                <Form.Label>How often do you run payroll?</Form.Label>

                <Form.Select
                  name="payrollFrequency"
                  value={values.payrollFrequency}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={
                    touched.payrollFrequency && !!errors.payrollFrequency
                  }
                  isValid={touched.payrollFrequency && !errors.payrollFrequency}
                >
                  <option value="">Select Frequency</option>
                  <option value={WEEKLY}>Every Week</option>
                  <option value={EVERY_OTHER_WEEK}>Every Two Weeks</option>
                  <option value={TWICE_PER_MONTH}>Twice a Month</option>
                  <option value={MONTHLY}>Every Month</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.payrollFrequency}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            {showDayOne(values.payrollFrequency) && (
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="formGroupDayone">
                  <Form.Label>
                    {values.payrollFrequency === MONTHLY
                      ? "What day of the month is the payday?"
                      : "What day of the month is the first payday?"}
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error preserve existing quirk: `sm` is not a
                    // valid Form.Select prop and has no runtime effect.
                    sm={6}
                    name="dayOne"
                    value={values.dayOne}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.dayOne && !!errors.dayOne}
                    isValid={touched.dayOne && !errors.dayOne}
                  >
                    <option value="" disabled>
                      Day of Month
                    </option>
                    {daysOfMonthOptions.map((item) => (
                      <option value={item.value} key={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Row>
            )}
            {showDayTwo(values.payrollFrequency) && (
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="formGroupDayTwo">
                  <Form.Label>
                    What day of the month is the second payday?
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error preserve existing quirk: `sm` is not a
                    // valid Form.Select prop and has no runtime effect.
                    sm={6}
                    name="dayTwo"
                    value={values.dayTwo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.dayTwo && !!errors.dayTwo}
                    isValid={touched.dayTwo && !errors.dayTwo}
                  >
                    <option value="" disabled>
                      Day of Month
                    </option>
                    {daysOfMonthOptions.map((item) => (
                      <option value={item.value} key={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Row>
            )}
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formBasicDateOfBirth">
                <Form.Label>When is your next pay day?</Form.Label>
                <div>
                  <p className="start-date">Start Date:</p>
                </div>
                <div className="date-picker">
                  <span className="calendar">
                    <FiCalendar
                      size={"18px"}
                      color={"#FFFFFF"}
                      fill={"#FFFFFF"}
                      stroke={"#AAB1BF"}
                      strokeWidth={"2"}
                    />
                  </span>
                  <DatePicker
                    className="form-control"
                    name="anchorPayDate"
                    // parseDate returns a numeric timestamp at runtime; the
                    // DatePicker `value` prop is typed as a string. Cast at the
                    // boundary to preserve the existing loose usage.
                    value={
                      parseDate(values.anchorPayDate) as string | undefined
                    }
                    dateFormat="MM/dd/yyyy"
                    selected={
                      (values.anchorPayDate &&
                        new Date(values.anchorPayDate)) ||
                      null
                    }
                    onChange={(val: Date | null) => {
                      void setFieldValue(
                        "anchorPayDate",
                        new Date(val as Date)
                      );
                    }}
                    onBlur={handleBlur}
                    // @ts-expect-error preserve existing quirk: `isInvalid`/`isValid`
                    // are react-bootstrap props with no effect on DatePicker.
                    isInvalid={touched.anchorPayDate && !!errors.anchorPayDate}
                    isValid={touched.anchorPayDate && !errors.anchorPayDate}
                  />
                </div>
                {errors.anchorPayDate && touched.anchorPayDate && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {errors.anchorPayDate}
                  </div>
                )}
              </Form.Group>
            </Row>
            {this.props.enabledDaysBeforeDebit && (
              <Row className="mb-3">
                <Form.Group as={Col} controlId="formGroupProcessingOffset">
                  <Form.Label>
                    How many days prior to the payroll date should approved
                    contributions be debited from the linked bank account?
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error preserve existing quirk: `sm` is not a
                    // valid Form.Select prop and has no runtime effect.
                    sm={6}
                    name="payrollProcessingOffset"
                    value={values.payrollProcessingOffset}
                    onChange={(e) =>
                      void setFieldValue(
                        "payrollProcessingOffset",
                        +e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    isInvalid={
                      touched.payrollProcessingOffset &&
                      !!errors.payrollProcessingOffset
                    }
                    isValid={
                      touched.payrollProcessingOffset &&
                      !errors.payrollProcessingOffset
                    }
                  >
                    <option value="">Select Number of Days</option>
                    {payrollProcessingOffset.map((item) => (
                      <option value={item.value} key={item.key}>
                        {item.description}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.payrollProcessingOffset}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            )}
            <Row className="mb-3">
              <Form.Group as={Col} sm={12} controlId="useDisplayDates">
                <div className="d-flex align-items-center">
                  <Form.Check
                    className="useDisplayDates"
                    type="checkbox"
                    name="useDisplayDates"
                    checked={values.useDisplayDates}
                    // @ts-expect-error preserve existing quirk: a boolean is
                    // passed to the checkbox `value` prop (expects a string).
                    value={values.useDisplayDates}
                    label="Pay period ends before payday"
                    onChange={(e) => {
                      void setFieldValue("useDisplayDates", e.target.checked);
                      if (!e.target.checked) {
                        void setFieldValue("displayDayOne", "");
                        void setFieldValue("displayDayTwo", "");
                        void setFieldValue("displayAnchorPayDate", null);
                      }
                    }}
                  />
                  <InfoTooltip
                    tooltipBody="If checked, you can configure the pay period end date."
                    placement="right"
                  />
                </div>
              </Form.Group>
            </Row>
            {values.useDisplayDates && showDayOne(values.payrollFrequency) && (
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="formGroupDisplayDayOne">
                  <Form.Label>
                    {values.payrollFrequency === MONTHLY
                      ? "What day of the month is the end of the pay period?"
                      : "What day of the month is the first end of the pay period?"}
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error preserve existing quirk: `sm` is not a
                    // valid Form.Select prop and has no runtime effect.
                    sm={6}
                    name="displayDayOne"
                    value={values.displayDayOne}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.displayDayOne && !!errors.displayDayOne}
                    isValid={touched.displayDayOne && !errors.displayDayOne}
                  >
                    <option value="" disabled>
                      Day of Month
                    </option>
                    {daysOfMonthOptions.map((item) => (
                      <option value={item.value} key={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.displayDayOne}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            )}
            {values.useDisplayDates && showDayTwo(values.payrollFrequency) && (
              <Row className="mb-3">
                <Form.Group as={Col} md={6} controlId="formGroupDayTwo">
                  <Form.Label>
                    What day of the month is the second end of the pay period?
                  </Form.Label>
                  <Form.Select
                    // @ts-expect-error preserve existing quirk: `sm` is not a
                    // valid Form.Select prop and has no runtime effect.
                    sm={6}
                    name="displayDayTwo"
                    value={values.displayDayTwo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.displayDayTwo && !!errors.displayDayTwo}
                    isValid={touched.displayDayTwo && !errors.displayDayTwo}
                  >
                    <option value="" disabled>
                      Day of Month
                    </option>
                    {daysOfMonthOptions.map((item) => (
                      <option value={item.value} key={item.key}>
                        {item.value}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.displayDayTwo}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            )}
            {values.useDisplayDates &&
              !showDayTwo(values.payrollFrequency) &&
              !showDayOne(values.payrollFrequency) && (
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="displayAnchorPayDate">
                    <Form.Label>
                      What is the last day of your current pay period?
                    </Form.Label>

                    <div>
                      <div>
                        <p className="start-date">End Date:</p>
                      </div>
                      <div className="date-picker">
                        <span className="calendar">
                          <FiCalendar
                            size={"18px"}
                            color={"#FFFFFF"}
                            fill={"#FFFFFF"}
                            stroke={"#AAB1BF"}
                            strokeWidth={"2"}
                          />
                        </span>
                        <DatePicker
                          className="form-control"
                          name="displayAnchorPayDate"
                          value={
                            (isNil(values.displayAnchorPayDate)
                              ? parseDate(values.anchorPayDate)
                              : parseDate(values.displayAnchorPayDate)) as
                              | string
                              | undefined
                          }
                          dateFormat="MM/dd/yyyy"
                          selected={
                            (values.displayAnchorPayDate &&
                              new Date(values.displayAnchorPayDate)) ||
                            null
                          }
                          onChange={(val: Date | null) => {
                            void setFieldValue(
                              "displayAnchorPayDate",
                              new Date(val as Date)
                            );
                          }}
                          onBlur={handleBlur}
                          // @ts-expect-error preserve existing quirk: `isInvalid`/`isValid`
                          // are react-bootstrap props with no effect on DatePicker.
                          isInvalid={
                            touched.displayAnchorPayDate &&
                            !!errors.displayAnchorPayDate
                          }
                          isValid={
                            touched.displayAnchorPayDate &&
                            !errors.displayAnchorPayDate
                          }
                          maxDate={
                            // parseDate yields a numeric timestamp at runtime;
                            // maxDate is typed as Date. Cast at the boundary to
                            // preserve the existing loose usage.
                            (isNil(values.anchorPayDate)
                              ? values.anchorPayDate
                              : parseDate(values.anchorPayDate)) as
                              | Date
                              | undefined
                          }
                        />
                      </div>
                    </div>
                    {errors.displayAnchorPayDate &&
                      touched.displayAnchorPayDate && (
                        <div
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        >
                          {errors.displayAnchorPayDate}
                        </div>
                      )}
                  </Form.Group>
                </Row>
              )}
            {this.props.hasExpiredInvites && (
              <Row className="mb-3">
                <Form.Group as={Col} sm={12} controlId="formBasicUniqueCode">
                  <div className="d-flex align-items-center">
                    <Form.Check
                      className="useUniqueInviteCodes"
                      type="checkbox"
                      name="useUniqueInviteCodes"
                      checked={values.useUniqueInviteCodes}
                      // @ts-expect-error preserve existing quirk: a boolean is
                      // passed to the checkbox `value` prop (expects a string).
                      value={values.useUniqueInviteCodes}
                      label="Employee invites should expire"
                      onChange={(e) => {
                        void setFieldValue(
                          "useUniqueInviteCodes",
                          e.target.checked
                        );
                      }}
                    />
                    <InfoTooltip
                      tooltipBody="If checked, we will generate a unique invite code for each employee that will expire after 60 days."
                      placement="right"
                    />
                  </div>
                </Form.Group>
              </Row>
            )}
            {hasMinimumScheduleValues(values) && (
              // hasMinimumScheduleValues guarantees payrollFrequency is set at
              // runtime; parseDayString/formatDate return `... | null` while the
              // (typed) PayPeriodExamples props are `... | undefined`. Cast each
              // value at this boundary to preserve the existing runtime behavior.
              <PayPeriodExamples
                payrollFrequency={values.payrollFrequency as string}
                dayOne={parseDayString(values.dayOne) as number | undefined}
                dayTwo={parseDayString(values.dayTwo) as number | undefined}
                anchorPayDate={
                  formatDate(values.anchorPayDate) as string | undefined
                }
                displayDayOne={
                  parseDayString(values.displayDayOne) as number | undefined
                }
                displayDayTwo={
                  parseDayString(values.displayDayTwo) as number | undefined
                }
                displayAnchorPayDate={
                  formatDate(values.displayAnchorPayDate) as string | undefined
                }
                payrollProcessingOffset={values.payrollProcessingOffset}
              />
            )}
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <div className="submit-row">
              <Button
                btnLabel="Cancel"
                name="cancel"
                color="cancel"
                type="button"
                onClick={this.props.onClose}
              />

              <Button
                btnLabel="Add Group"
                name="submit"
                withArrow={true}
                loading={this.props.isLoading}
              />
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}
